import { Validators } from '@angular/forms';
import {
  Component,
  Input,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Store } from '@ngrx/store';

import { TranslateService } from '@ngx-translate/core';

import { LoggerService } from '@services/logger.service';
import { FormsService } from 'src/app/core/services/forms.service';

import { GeneralDynamicFormComponent } from '../../../general-dynamic-form/general-dynamic-form.component';

import { NomObjectTypes } from 'src/app/core/constants';
import { DataObject } from 'src/app/core/models/core.models';
import { FormProperty } from 'src/app/core/models/form.models';

import { AppState } from 'src/app/reducers';

import { selectActiveWorkspaceId } from 'src/app/core/reducers';
import { UserWorkspacesService } from 'src/app/core/services/user-workspaces.service';

import { map, take, tap } from 'rxjs';

@Component({
  selector: 'app-feed-duplicate-form',
  templateUrl: './feed-duplicate-form.component.html',
  styleUrls: ['./feed-duplicate-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FeedDuplicateFormComponent extends GeneralDynamicFormComponent {
  private userWorkspaces = inject(UserWorkspacesService);

  @Input() objects: DataObject[];
  @Input() hasDimensionInSelection: boolean;

  constructor(
    protected store: Store<AppState>,
    protected formService: FormsService,
    protected translateService: TranslateService,
    protected logger: LoggerService,
  ) {
    super(formService, store, logger);
  }

  public activeFormProperties: FormProperty[] = [];
  public formProperties: FormProperty[] = [];

  ngOnInit() {
    this.formProperties = [
      {
        propertyName: 'workspace_id',
        validators: [Validators.required],
        propertyLabel: this.translateService.instant(
          'feed-duplicate-form.label.target-workspace',
        ),
        controlType: 'select',
        valueOptionsAsync: this.userWorkspaces.sortedWorkspaces$().pipe(
          map((workspaces) =>
            workspaces.map((workspace) => ({
              value: workspace.id,
              name: workspace.title,
            })),
          ),
        ),
      },
      {
        propertyName: 'options',
        controlType: 'checkbox-array',
        propertyGroup: {
          key: 'feed-duplicate-form.group.copy-options',
          style: 'copy-options',
        },
        additionalConfig: {
          values: this.formService.duplicateFeedOptions,
        },
        shouldDisplay: (formGroup) => {
          return !this.hasDimensionInSelection;
        },
      },
    ];

    if (this.objects.length === 1) {
      this.formProperties.unshift(
        {
          propertyName: 'title',
          propertyLabel: this.translateService.instant(
            'feed-duplicate-form.label.title',
          ),
          controlType: 'text',
          additionalConfig: {
            placeholder: this.translateService.instant(
              'feed-duplicate-form.placeholder.title',
            ),
          },
          validators: [Validators.required],
        },
        {
          propertyName: 'description',
          propertyLabel: this.translateService.instant(
            'feed-duplicate-form.label.description',
          ),
          controlType: 'textarea',
          additionalConfig: {
            placeholder: this.translateService.instant(
              'feed-duplicate-form.placeholder.description',
            ),
            rows: 4,
          },
        },

        {
          propertyName: 'object_type_id',
          controlType: 'select',
          propertyLabel: this.translateService.instant(
            'feed-duplicate-form.label.feed-type',
          ),
          shouldDisplay: (formGroup) => {
            return !this.hasDimensionInSelection;
          },
          valueOptions: [
            {
              value: NomObjectTypes.ONLINE_FEED,
              name: this.translateService.instant(
                'feed-duplicate-form.value-opion.online-feed',
              ),
            },
            {
              value: NomObjectTypes.SOCIAL_FEED,
              name: this.translateService.instant(
                'feed-duplicate-form.value-opion.social-feed',
              ),
            },
            {
              value: NomObjectTypes.BROADCAST_FEED,
              name: this.translateService.instant(
                'feed-duplicate-form.value-opion.broadcast-feed',
              ),
            },
            {
              value: NomObjectTypes.COMBINED_FEED,
              name: this.translateService.instant(
                'feed-duplicate-form.value-opion.combined-feed',
              ),
            },
            {
              value: NomObjectTypes.CLIPPING_FEED,
              name: this.translateService.instant(
                'feed-duplicate-form.value-opion.clipping-feed',
              ),
            },
            {
              value: NomObjectTypes.STORY_FEED,
              name: this.translateService.instant(
                'feed-duplicate-form.value-opion.story-feed',
              ),
            },
          ],
        },
      );
    } else {
      this.formProperties.unshift({
        propertyName: 'prefix',
        propertyLabel: this.translateService.instant(
          'feed-duplicate-form.label.name-prefix',
        ),
        controlType: 'text',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'feed-duplicate-form.placeholder.name-prefix',
          ),
        },
        validators: [Validators.required],
      });
    }

    super.ngOnInit();

    this.store
      .select(selectActiveWorkspaceId)
      .pipe(
        take(1),
        tap((workspaceId) => {
          this.formService.populateFormValues(this.formGroup, {
            workspace_id: workspaceId,
          });
        }),
      )
      .subscribe();

    if (this.objects.length == 1) {
      this.formService.populateFormValues(this.formGroup, {
        title: 'Copy of ' + this.objects[0].title,
        description: this.objects[0].description,
        object_type_id: this.objects[0].object_type_id,
        workspace_id: this.objects[0].workspace_id,
      });
    } else {
      this.formService.populateFormValues(this.formGroup, {
        prefix: 'Copy of ',
      });
    }

    this.formService.populateFormValues(this.formGroup, {
      options: [
        'duplicateWidgets',
        'duplicateExportSchedules',
        'duplicateAlerts',
        'duplicateOutputSettings',
        'duplicateContentFilters',
        'duplicateRemovedItems',
        'preserveLinkedDimensions',
        'duplicatePermissions',
      ],
    });
  }
}
